import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { getDisplayName } from '../../../../models/NameUtils';
import { useSchedulerConfigsQuery } from '../../../../queries/Scheduler/useSchedulerConfigsQuery';
import { Checkbox } from '../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../../components-v2/Combobox';
import { ComboboxAvatarItem } from '../../../components-v2/Combobox/ComboboxAvatarItem';
import { Input } from '../../../components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/Select';
import { styleSheet } from './styles';

export interface IMemberFormData {
	id: string;
	tempId: string;
	userId: string;
	meetingConfigId: string;
	priority: number;
	enabled: boolean;
}

interface IUserMeetingLinkSelectorProps {
	member: IMemberFormData;
	users: Api.IUser[];
	index: number;
	onUpdate: (tempId: string, updates: Partial<IMemberFormData>) => void;
	onRemove: (tempId: string) => void;
	isSelected?: boolean;
	onSelectionChange?: (meetingConfigId: string, selected: boolean) => void;
}

export function UserMeetingLinkSelector({
	member,
	users,
	index,
	onUpdate,
	onRemove,
	isSelected = false,
	onSelectionChange,
}: IUserMeetingLinkSelectorProps) {
	const [userSearchValue, setUserSearchValue] = React.useState('');

	// Find selected user object
	const selectedUser = React.useMemo(() => {
		return users.find(u => u.id === member.userId) ?? null;
	}, [users, member.userId]);

	// Fetch meeting configs for selected user
	const { data: meetingConfigsData, isLoading: isLoadingConfigs } = useSchedulerConfigsQuery({
		userId: member.userId,
		enabled: Boolean(member.userId),
	});

	// Filter to only published configs
	const availableMeetingConfigs = React.useMemo(() => {
		return meetingConfigsData?.values?.filter(config => config.published) ?? [];
	}, [meetingConfigsData]);

	// Get the selected meeting config's display name
	const selectedMeetingName = React.useMemo(() => {
		if (!member.meetingConfigId) return null;
		const config = availableMeetingConfigs.find(c => c.id === member.meetingConfigId);
		return config?.published?.name ?? config?.draft?.name ?? null;
	}, [member.meetingConfigId, availableMeetingConfigs]);

	// Status message for combobox
	const userSearchStatus = React.useMemo(() => {
		if (users.length === 0) return 'Loading users...';
		if (userSearchValue && users.length === 0) return `No users found for "${userSearchValue}"`;
		return `${users.length} user${users.length === 1 ? '' : 's'} available`;
	}, [users.length, userSearchValue]);

	const handleUserSelected = (user: Api.IUser | null) => {
		if (user) {
			setUserSearchValue(getDisplayName(user));
			// Reset meeting config when user changes
			onUpdate(member.tempId, { userId: user.id, meetingConfigId: '', id: '' });
		}
	};

	const handleMeetingConfigChange = (value: string) => {
		onUpdate(member.tempId, { meetingConfigId: value, id: value });
	};

	const handlePriorityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const priority = parseInt(e.target.value, 10) || 1;
		onUpdate(member.tempId, { priority });
	};

	const handleEnabledChange = (checked: boolean | 'indeterminate') => {
		onUpdate(member.tempId, { enabled: checked === true });
	};

	const handleSelectionChange = (checked: boolean | 'indeterminate') => {
		if (member.meetingConfigId && onSelectionChange) {
			onSelectionChange(member.meetingConfigId, checked === true);
		}
	};

	const canBeSelected = Boolean(member.meetingConfigId);

	// Initialize search value when editing existing member
	React.useEffect(() => {
		if (selectedUser && !userSearchValue) {
			setUserSearchValue(getDisplayName(selectedUser));
		}
	}, [selectedUser, userSearchValue]);

	return (
		<div className={css(styleSheet.memberRow)}>
			{onSelectionChange && (
				<Checkbox
					styles={[styleSheet.memberSelectionCheckbox]}
					checked={isSelected}
					onCheckedChange={handleSelectionChange}
					disabled={!canBeSelected}
					aria-label={canBeSelected ? 'Select member for bulk settings' : 'Select a meeting link first'}
				/>
			)}
			<span className={css(styleSheet.memberNumber)}>{index + 1}.</span>
			<div className={css(styleSheet.memberFields)}>
				<div className={css(styleSheet.memberFieldRow)}>
					<span className={css(styleSheet.memberFieldLabel)}>User:</span>
					<div className={css(styleSheet.memberFieldCombobox)}>
						<Combobox
							items={users}
							value={selectedUser}
							onValueChange={handleUserSelected}
							inputValue={userSearchValue}
							onInputValueChange={setUserSearchValue}
							itemToStringLabel={(user: Api.IUser) => getDisplayName(user)}
							isItemEqualToValue={(item: Api.IUser, value: Api.IUser) => item.id === value.id}
						>
							<ComboboxInput placeholder='Search user...' styles={[styleSheet.comboboxInput]} />
							<ComboboxContent>
								<ComboboxStatus>{userSearchStatus}</ComboboxStatus>
								<ComboboxList>
									{(user: Api.IUser) => (
										<ComboboxAvatarItem
											key={user.id}
											value={user}
											avatarImageUrl={user.profilePic}
											avatarFallbackText={
												(user.firstName?.substring(0, 1) ?? '') + (user.lastName?.substring(0, 1) ?? '')
											}
											title={getDisplayName(user)}
											description={user.email}
										/>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
				</div>

				<div className={css(styleSheet.memberFieldRow)}>
					<span className={css(styleSheet.memberFieldLabel)}>Meeting:</span>
					<div className={css(styleSheet.memberFieldSelect)}>
						<Select
							value={member.meetingConfigId}
							onValueChange={handleMeetingConfigChange}
							disabled={!member.userId || isLoadingConfigs}
						>
							<SelectTrigger styles={styleSheet.selectTrigger}>
								<SelectValue
									placeholder={
										!member.userId
											? 'Select a user first...'
											: isLoadingConfigs
												? 'Loading...'
												: 'Select a meeting link...'
									}
								>
									{selectedMeetingName}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								{availableMeetingConfigs.map(config => (
									<SelectItem key={config.id} value={config.id}>
										{config.published?.name ?? config.draft?.name ?? 'Unnamed'}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<div className={css(styleSheet.memberFieldRow)}>
					<span className={css(styleSheet.memberFieldLabel)}>Priority:</span>
					<Input
						type='number'
						styles={[styleSheet.priorityInput]}
						value={member.priority}
						onChange={handlePriorityChange}
						min={1}
					/>
					<span className={css(styleSheet.memberFieldLabel)}>Enabled:</span>
					<Checkbox
						styles={[styleSheet.enabledCheckbox]}
						checked={member.enabled}
						onCheckedChange={handleEnabledChange}
					/>
				</div>
			</div>
			<button
				type='button'
				className={css(styleSheet.removeButton)}
				onClick={() => onRemove(member.tempId)}
				aria-label='Remove member'
			>
				&times;
			</button>
		</div>
	);
}
